import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Camera, ScanLine, Utensils, Check, X, Users, ChevronUp, ChevronDown, Clock, Wine, Flame, Waves, Mountain } from "lucide-react";
import { api, formatDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const WEEKDAY = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long" }); }
  catch { return ""; }
};
const paxOf = (b) => b.party_size || 1;
const COURSES = [
  { key: "canapes", label: "Canapés" },
  { key: "starter", label: "Starter" },
  { key: "main", label: "Main" },
  { key: "dessert", label: "Dessert" },
];
const JOURNEY_META = {
  fire: { label: "Fire", Icon: Flame, color: "text-orange-400" },
  ocean: { label: "Ocean", Icon: Waves, color: "text-sky-400" },
  earth: { label: "Earth", Icon: Mountain, color: "text-lime-400" },
};

export default function RestaurantCheckin() {
  const { token } = useParams();
  const scannerRef = useRef(null);
  const modalRef = useRef(false);
  const busyRef = useRef(false);
  const touchStartY = useRef(null);
  const audioRef = useRef(null);
  const [access, setAccess] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanModal, setScanModal] = useState(null);
  const [camError, setCamError] = useState("");
  const [toggling, setToggling] = useState({});
  const [selDate, setSelDate] = useState("");
  const [selSeating, setSelSeating] = useState("");

  const loadRoster = useCallback(async () => {
    try {
      const r = await api.get(`/restaurant/${token}/roster`);
      setAccess(r.data.access);
      setBookings(r.data.bookings || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load the dining list.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const dates = Array.from(new Set(bookings.map((b) => b.date))).sort();
  useEffect(() => {
    if (!selDate && dates.length) {
      const todayISO = new Date().toLocaleDateString("en-CA");
      setSelDate(dates.find((d) => d >= todayISO) || dates[dates.length - 1]);
    }
  }, [dates, selDate]);

  const seatingsForDate = Array.from(new Set(bookings.filter((b) => b.date === selDate).map((b) => b.seating))).sort();
  useEffect(() => {
    if (selDate && seatingsForDate.length && !seatingsForDate.includes(selSeating)) {
      setSelSeating(seatingsForDate[0]);
    }
  }, [selDate, seatingsForDate, selSeating]);

  const rows = bookings
    .filter((b) => b.date === selDate && b.seating === selSeating)
    .sort((a, b) => {
      const ao = a.seat_order == null ? 1e9 : a.seat_order;
      const bo = b.seat_order == null ? 1e9 : b.seat_order;
      return ao - bo || (a.customer?.full_name || "").localeCompare(b.customer?.full_name || "");
    });
  const checkedCount = rows.filter((b) => b.checked_in).length;
  const totalPax = rows.reduce((s, b) => s + paxOf(b), 0);
  const checkedPax = rows.filter((b) => b.checked_in).reduce((s, b) => s + paxOf(b), 0);
  const noShowCount = rows.filter((b) => b.no_show).length;

  const seatingCount = (date, seating) => {
    const r = bookings.filter((b) => b.date === date && b.seating === seating);
    return { checked: r.filter((b) => b.checked_in).length, total: r.length };
  };

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current; scannerRef.current = null;
    if (s) { try { await s.stop(); } catch (_) {} try { await s.clear(); } catch (_) {} }
  }, []);

  const ensureAudio = () => {
    try {
      if (!audioRef.current) { const AC = window.AudioContext || window.webkitAudioContext; if (AC) audioRef.current = new AC(); }
      if (audioRef.current?.state === "suspended") audioRef.current.resume();
    } catch (_) {}
  };
  const feedback = (success) => {
    try { navigator.vibrate?.(success ? 80 : [50, 60, 50]); } catch (_) {}
    try {
      const ctx = audioRef.current; if (!ctx) return;
      const now = ctx.currentTime;
      const tones = success ? [880, 1320] : [300];
      tones.forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
        const start = now + i * 0.12; const dur = success ? 0.11 : 0.35;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        o.start(start); o.stop(start + dur + 0.02);
      });
    } catch (_) {}
  };

  const showResult = (r) => {
    modalRef.current = true;
    setScanModal(r);
    feedback(r.status === "ok" || r.status === "already");
    try { scannerRef.current?.pause(true); } catch (_) {}
  };
  const dismissResult = useCallback(() => {
    modalRef.current = false;
    setScanModal(null);
    try { scannerRef.current?.resume(); } catch (_) {}
  }, []);

  const processScan = useCallback(async (rawId) => {
    const m = String(rawId).match(/checkin\/([0-9a-fA-F-]{6,})/);
    const id = m ? m[1] : String(rawId).trim();
    busyRef.current = true; setBusy(true);
    try {
      const r = await api.post(`/restaurant/${token}/scan/${id}`, null, { params: { seating: selSeating || undefined } });
      const s = r.data.status;
      showResult({ status: s, name: r.data.name, booking: r.data.booking, message: s === "ok" ? "" : r.data.message || "" });
      if (s === "ok" || s === "already") await loadRoster();
    } catch (e) {
      showResult({ status: "not_found", message: formatApiErrorDetail(e.response?.data?.detail) || "Scan failed." });
    }
    busyRef.current = false; setBusy(false);
  }, [token, selSeating, loadRoster]);

  const checkInAnyway = async (b) => {
    setBusy(true);
    try {
      await api.post(`/restaurant/${token}/checkin/${b.id}`, { checked_in: true });
      showResult({ status: "ok", name: b.customer?.full_name, booking: { ...b, checked_in: true } });
      await loadRoster();
    } catch (e) {
      showResult({ status: "unpaid", name: b.customer?.full_name, message: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const startScanner = useCallback(async () => {
    setCamError("");
    await stopScanner();
    try {
      const h5 = new Html5Qrcode("resto-qr-reader", { verbose: false });
      scannerRef.current = h5;
      await h5.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => { if (modalRef.current || busyRef.current) return; await processScan(decodedText); }, () => {});
    } catch (e) {
      setCamError("Unable to access the camera. Allow camera permission or tick guests off the list below.");
    }
  }, [stopScanner, processScan]);

  useEffect(() => {
    if (scanning) startScanner(); else stopScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const toggleCheck = async (b) => {
    const next = !b.checked_in;
    setToggling((t) => ({ ...t, [b.id]: true }));
    setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, checked_in: next, no_show: next ? false : x.no_show } : x)));
    try { await api.post(`/restaurant/${token}/checkin/${b.id}`, { checked_in: next }); }
    catch (e) {
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, checked_in: !next } : x)));
      showResult({ status: "unpaid", name: b.customer?.full_name, message: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setToggling((t) => { const n = { ...t }; delete n[b.id]; return n; }); }
  };

  const markNoShow = async (b) => {
    const next = !b.no_show;
    setToggling((t) => ({ ...t, [b.id]: true }));
    setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, no_show: next, checked_in: next ? false : x.checked_in } : x)));
    try { await api.post(`/restaurant/${token}/no-show/${b.id}`, { no_show: next }); }
    catch (e) {
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, no_show: !next } : x)));
      showResult({ status: "unpaid", name: b.customer?.full_name, message: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setToggling((t) => { const n = { ...t }; delete n[b.id]; return n; }); }
  };

  const toggleService = async (b, key) => {
    const next = !(b.service && b.service[key]);
    setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, service: { ...(x.service || {}), [key]: next } } : x)));
    try { await api.post(`/restaurant/${token}/service/${b.id}`, { item: key, served: next }); }
    catch (e) {
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, service: { ...(x.service || {}), [key]: !next } } : x)));
      showResult({ status: "unpaid", name: b.customer?.full_name, message: formatApiErrorDetail(e.response?.data?.detail) || "Could not update." });
    }
  };

  const reorder = async (b, direction) => {
    try {
      await api.post(`/restaurant/${token}/reorder/${b.id}`, { direction });
      await loadRoster();
    } catch (_) { toast.error("Could not move guest"); }
  };

  if (loading) return <div className="min-h-screen bg-background grain flex items-center justify-center"><Loader2 className="animate-spin text-white" size={28} /></div>;
  if (error) return (
    <div data-testid="restaurant-error" className="min-h-screen bg-background grain flex items-center justify-center px-6">
      <div className="max-w-sm text-center">
        <XCircle className="text-[#C44536] mx-auto mb-4" size={36} />
        <p className="font-display font-extrabold uppercase tracking-tight text-xl mb-2">Access link not valid</p>
        <p className="text-sm text-gray-400">{error}</p>
      </div>
    </div>
  );

  return (
    <div data-testid="restaurant-checkin-page" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          <span className="font-display font-extrabold text-lg uppercase tracking-tighter inline-flex items-center gap-2"><Utensils size={18} className="text-amber-400" /> Dining Experience</span>
          <span data-testid="restaurant-name" className="text-xs uppercase tracking-[0.2em] text-gray-400 truncate">{access?.name}</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Date selector */}
        {dates.length > 1 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1" data-testid="resto-date-tabs">
            {dates.map((d) => (
              <button key={d} data-testid={`resto-date-${d}`} onClick={() => setSelDate(d)}
                className={`shrink-0 px-4 py-2.5 border text-xs uppercase tracking-[0.12em] font-bold transition-colors ${selDate === d ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                {WEEKDAY(d).slice(0, 3)} {formatDate(d)}
              </button>
            ))}
          </div>
        )}

        {/* Seating tabs */}
        <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-2">Seating time</p>
        <div className="flex gap-2 mb-5 flex-wrap" data-testid="resto-seating-tabs">
          {seatingsForDate.length === 0 && <p className="text-sm text-gray-500">No paid diners for this day yet.</p>}
          {seatingsForDate.map((s) => {
            const c = seatingCount(selDate, s);
            const active = selSeating === s;
            return (
              <button key={s} data-testid={`resto-seating-${s}`} onClick={() => setSelSeating(s)}
                className={`inline-flex items-center gap-2 px-5 py-3 border text-sm font-bold uppercase tracking-[0.1em] transition-colors ${active ? "bg-amber-500 text-black border-amber-500" : "border-amber-500/25 text-amber-200 hover:border-amber-400/60"}`}>
                <Clock size={14} /> {s}
                <span className={`text-[11px] ${active ? "text-black/60" : "text-gray-500"}`}>{c.checked}/{c.total}</span>
              </button>
            );
          })}
        </div>

        {/* Progress + scan toggle */}
        {selSeating && (
          <div className="bg-[#0C0C0E] border border-white/10 p-5 mb-5 flex items-center justify-between gap-4">
            <div>
              <p data-testid="resto-progress" className="font-display font-extrabold text-2xl">{checkedCount}<span className="text-gray-600 text-lg">/{rows.length}</span> <span className="text-sm text-gray-500 font-normal">seated · {selSeating}</span></p>
              <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mt-1">{checkedPax} of {totalPax} guests{noShowCount > 0 ? <span className="text-[#C44536]"> · {noShowCount} no-show{noShowCount !== 1 ? "s" : ""}</span> : null}</p>
            </div>
            <button data-testid="resto-toggle-scanner-btn" onClick={() => { ensureAudio(); setScanning((s) => !s); }}
              className={`uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 ${scanning ? "border border-white/30 text-white hover:bg-white/10" : "bg-white text-black hover:bg-gray-200"}`}>
              <ScanLine size={15} /> {scanning ? "Stop" : "Scan QR"}
            </button>
          </div>
        )}

        {/* Camera */}
        {scanning && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3 text-center">Point at the guest's QR code{busy ? " · checking…" : ""}</p>
            <div id="resto-qr-reader" data-testid="resto-qr-reader" className="w-full overflow-hidden border border-white/15 bg-black rounded-sm" style={{ minHeight: 280 }} />
            {camError && (
              <div className="mt-4 p-4 border border-[#C44536]/40 bg-[#2a1210]">
                <div className="flex items-start gap-3"><Camera size={18} className="text-[#C44536] mt-0.5" /><p className="text-sm text-gray-300">{camError}</p></div>
                <button data-testid="resto-retry-camera-btn" onClick={startScanner} className="mt-3 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-2.5 px-5 transition-colors inline-flex items-center gap-2"><Camera size={14} /> Retry Camera</button>
              </div>
            )}
          </div>
        )}

        {/* Roster */}
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Guest list · {selSeating} sitting</p>
        {selSeating && rows.length === 0 && <p className="px-4 py-6 text-sm text-gray-500 text-center border border-white/10">No paid guests for this sitting yet.</p>}
        <div className="border border-white/10 divide-y divide-white/5" data-testid="resto-roster">
          {rows.map((b, i) => {
            const journeys = Object.entries(b.journeys || {}).filter(([, n]) => n > 0);
            return (
            <div key={b.id} data-testid={`resto-row-${b.id}`} className={`px-4 py-4 transition-colors ${b.no_show ? "bg-[#2a1210]" : b.checked_in ? "bg-emerald-950/15" : ""}`}>
              <div className="flex items-center gap-3">
                <div className="shrink-0 flex flex-col items-center">
                  <button type="button" data-testid={`resto-up-${b.id}`} onClick={() => reorder(b, "up")} disabled={i === 0}
                    title="Move up" className="text-gray-500 hover:text-white disabled:opacity-20 disabled:hover:text-gray-500"><ChevronUp size={18} /></button>
                  <span data-testid={`resto-pos-${b.id}`} className="text-[11px] font-bold text-gray-400 leading-none">{i + 1}</span>
                  <button type="button" data-testid={`resto-down-${b.id}`} onClick={() => reorder(b, "down")} disabled={i === rows.length - 1}
                    title="Move down" className="text-gray-500 hover:text-white disabled:opacity-20 disabled:hover:text-gray-500"><ChevronDown size={18} /></button>
                </div>
                <button type="button" data-testid={`resto-check-${b.id}`} onClick={() => toggleCheck(b)} disabled={!!toggling[b.id]}
                  className={`shrink-0 w-8 h-8 border flex items-center justify-center transition-colors ${b.checked_in ? "bg-emerald-500 border-emerald-500 text-black" : "border-white/30 text-transparent hover:border-white/60"}`}>
                  {toggling[b.id] ? <Loader2 size={15} className="animate-spin text-white" /> : <Check size={17} strokeWidth={3} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-base font-medium truncate ${b.no_show ? "text-[#e07a6c]" : "text-white"}`}>{b.customer?.full_name || "—"}</p>
                    {b.allergies ? (
                      <span data-testid={`resto-allergy-flag-${b.id}`} title={b.allergies}
                        className="inline-flex items-center gap-1 shrink-0 bg-[#C44536] text-white text-[10px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 leading-none">
                        <AlertTriangle size={11} strokeWidth={2.5} /> Allergy
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    {journeys.map(([k, n]) => { const m = JOURNEY_META[k]; const Ic = m.Icon; return (
                      <span key={k} className={`inline-flex items-center gap-1 text-[11px] ${m.color}`}><Ic size={11} /> {n}×{m.label}</span>
                    ); })}
                    {b.beverage_count > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-rose-300"><Wine size={11} /> {b.beverage_count} pairing{b.beverage_count !== 1 ? "s" : ""}</span>}
                  </div>
                </div>
                {/* Big obvious pax */}
                <div data-testid={`resto-pax-${b.id}`} className="shrink-0 flex flex-col items-center justify-center w-14 h-14 bg-white/5 border border-white/15">
                  <span className="font-display font-extrabold text-2xl leading-none text-white">{paxOf(b)}</span>
                  <span className="text-[9px] uppercase tracking-[0.1em] text-gray-500 mt-0.5">pax</span>
                </div>
                <button type="button" data-testid={`resto-no-show-${b.id}`} onClick={() => markNoShow(b)} disabled={!!toggling[b.id]}
                  title={b.no_show ? "Undo no-show" : "Mark as no-show"}
                  className={`shrink-0 w-8 h-8 border flex items-center justify-center transition-colors ${b.no_show ? "border-white/30 text-gray-400 hover:border-white/60 hover:text-white" : "border-white/15 text-gray-600 hover:border-[#C44536]/60 hover:text-[#C44536]"}`}>
                  <XCircle size={16} />
                </button>
              </div>

              {/* Allergies */}
              {b.allergies ? (
                <div data-testid={`resto-allergy-${b.id}`} className="mt-3 flex items-start gap-2 bg-[#C44536]/10 border border-[#C44536]/30 px-3 py-2">
                  <AlertTriangle size={14} className="text-[#e07a6c] mt-0.5 shrink-0" />
                  <p className="text-[13px] text-[#f0b5ac]"><span className="uppercase tracking-[0.1em] text-[10px] text-[#C44536] mr-1">Allergies</span>{b.allergies}</p>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-gray-600 uppercase tracking-[0.1em]">No allergies noted</p>
              )}
              {b.notes && <p className="mt-1.5 text-[12px] text-gray-400 italic">“{b.notes}”</p>}

              {/* Course service — food + its wine pairing per course */}
              <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid={`resto-courses-${b.id}`}>
                {COURSES.map((c) => {
                  const foodDone = !!(b.service && b.service[c.key]);
                  const wineKey = `${c.key}_wine`;
                  const wineDone = !!(b.service && b.service[wineKey]);
                  const hasWine = b.beverage_count > 0;
                  return (
                    <div key={c.key} className="flex flex-col gap-1.5">
                      <span className="text-[10px] uppercase tracking-[0.06em] text-gray-500 text-center">{c.label}</span>
                      <button type="button" data-testid={`resto-course-${b.id}-${c.key}`} onClick={() => toggleService(b, c.key)}
                        className={`flex items-center justify-center gap-1 py-2.5 border text-[10px] uppercase tracking-[0.06em] font-bold transition-colors ${foodDone ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200" : "border-white/15 text-gray-400 hover:border-white/50"}`}>
                        {foodDone ? <Check size={13} strokeWidth={3} /> : <Utensils size={12} />} Food
                      </button>
                      <button type="button" data-testid={`resto-course-${b.id}-${wineKey}`} onClick={() => hasWine && toggleService(b, wineKey)} disabled={!hasWine}
                        title={hasWine ? "Wine pairing for this course" : "No wine pairing on this booking"}
                        className={`flex items-center justify-center gap-1 py-2.5 border text-[10px] uppercase tracking-[0.06em] font-bold transition-colors ${!hasWine ? "border-white/5 text-gray-700 cursor-not-allowed" : wineDone ? "bg-rose-500/20 border-rose-400/50 text-rose-200" : "border-white/15 text-gray-400 hover:border-rose-400/50"}`}>
                        {wineDone ? <Check size={13} strokeWidth={3} /> : <Wine size={12} />} Wine
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ); })}
        </div>
      </div>

      {scanModal && (() => {
        const s = scanModal.status;
        const b = scanModal.booking;
        const ok = s === "ok";
        const other = s === "other_seating";
        const accent = ok ? "#34d399" : s === "already" ? "#eab308" : (s === "unpaid" || other) ? "#f59e0b" : "#C44536";
        const title = ok ? "Seated" : s === "already" ? "Already seated" : s === "unpaid" ? "Not paid yet" : other ? "Different sitting" : s === "wrong_place" ? "Not a dining pass" : "Not valid";
        return (
          <div data-testid="resto-scan-modal" onClick={dismissResult}
            onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
            onTouchEnd={(e) => { if (touchStartY.current != null && touchStartY.current - e.changedTouches[0].clientY > 55) dismissResult(); touchStartY.current = null; }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6 bg-black/90 backdrop-blur-sm animate-in fade-in duration-150">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#0C0C0E] border rounded-sm p-8 text-center" style={{ borderColor: `${accent}66` }}>
              <div className="w-20 h-20 rounded-full border-2 flex items-center justify-center mx-auto mb-6" style={{ borderColor: accent }}>
                {ok && <CheckCircle2 size={40} style={{ color: accent }} />}
                {s === "already" && <AlertTriangle size={40} style={{ color: accent }} />}
                {(s === "unpaid" || other) && <AlertTriangle size={40} style={{ color: accent }} />}
                {(s === "not_found" || s === "wrong_place") && <XCircle size={40} style={{ color: accent }} />}
              </div>
              <p data-testid="resto-scan-status" className="font-display font-extrabold uppercase tracking-tight text-2xl" style={{ color: accent }}>{title}</p>
              {scanModal.name && <p data-testid="resto-scan-name" className="text-white text-xl mt-3 font-medium">{scanModal.name}</p>}
              {b && (
                <div className="mt-6 border-t border-white/10 pt-6 space-y-3 text-left">
                  <div className="flex items-center justify-center gap-2">
                    <Users size={22} style={{ color: accent }} />
                    <span data-testid="resto-scan-pax" className="font-display font-extrabold text-3xl text-white">{paxOf(b)}</span>
                    <span className="text-gray-400 text-sm uppercase tracking-[0.15em]">{paxOf(b) === 1 ? "guest" : "guests"}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Sitting</span><span className="text-white text-right">{b.seating}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Date</span><span className="text-white text-right">{WEEKDAY(b.date)} {formatDate(b.date)}</span></div>
                  {b.beverage_count > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Wine pairings</span><span className="text-rose-300 text-right">{b.beverage_count}</span></div>}
                  {b.allergies ? (
                    <div className="flex items-start gap-2 bg-[#C44536]/10 border border-[#C44536]/30 px-3 py-2 mt-1">
                      <AlertTriangle size={14} className="text-[#e07a6c] mt-0.5 shrink-0" />
                      <p className="text-[13px] text-[#f0b5ac] text-left"><span className="uppercase tracking-[0.1em] text-[10px] text-[#C44536] mr-1">Allergies</span>{b.allergies}</p>
                    </div>
                  ) : null}
                </div>
              )}
              {scanModal.message && <p className="text-sm text-gray-400 mt-5">{scanModal.message}</p>}
              {other && b ? (
                <div className="mt-7 space-y-3">
                  <button data-testid="resto-checkin-anyway" onClick={() => checkInAnyway(b)} disabled={busy}
                    className="w-full py-4 uppercase tracking-[0.15em] text-xs font-bold text-black inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ backgroundColor: accent }}>
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Seat anyway
                  </button>
                  <button data-testid="resto-scan-cancel" onClick={dismissResult} className="w-full border border-white/20 text-white hover:bg-white/10 uppercase tracking-[0.15em] text-xs font-bold py-3.5 transition-colors">Cancel</button>
                </div>
              ) : (
                <button data-testid="resto-scan-done" onClick={dismissResult} className="mt-8 w-full bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2">
                  <X size={15} /> Done — scan next
                </button>
              )}
            </div>
            {!other && <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mt-6 inline-flex items-center gap-2 animate-pulse"><ChevronUp size={14} /> Swipe up to keep scanning</p>}
          </div>
        );
      })()}
    </div>
  );
}
